import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  InjectionToken,
  Input,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import {ComponentDefinition, DocumentChangedInfo} from '@formbird/types';
import {ChangedDocumentService} from 'formbird-services';
import flatpickr from 'flatpickr';
import {SclUtilDocumentService} from '../../../../formbird-sc-shared/src/lib/scl-util-document.service';
import {FormbirdInjectorService} from '../../../../formbird-services/src/lib/services/injector/formbird-injector.service';
import * as moment from 'moment';
import {DeviceDetectService} from '../../../../formbird-sc-shared/src/lib/device-detect.service';
import {ComponentSetupService} from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import {AbstractComponent} from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import {FinaliseValueFieldService} from '@services/document/finalise-value-field.service';
import {MatTooltip} from '@angular/material/tooltip';
import {MatFormField} from '@angular/material/form-field';

export const WINDOW_TOKEN = new InjectionToken<Window>('Window location object');
declare var global;
const logger = console;

interface DateTimeComponentDefinition extends ComponentDefinition {
  time_24hr?: boolean;
  caption?: string;
  style?: string;
  defaultNow?: string;
  noCalendar?: boolean;
  allowInput?: boolean;
  displayFormat?: string;
  finaliseValue?: any;
}

@Component({
  templateUrl: './date-time.component.html',
  styleUrls: ['./date-time.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [{provide: WINDOW_TOKEN, useValue: window}],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DateTimeComponent extends AbstractComponent<DateTimeComponentDefinition> implements OnInit {

  @Input() floatLabel: any;

  flatPicker: any;
  isMobileDetect: boolean;

  defaultDateValue: any;
  defaultHourValue: any;
  defaultMinValue: any;
  defaultSecValue: any;
  epochDocFieldValue: any;
  oldDateValue: any;

  modifiedFieldService: any;
  notificationService: any;
  currentDateStr;
  private displayFormat: string;

  debounceTimer: any;
  private hourInputValue: any;
  private minuteInputValue: any;
  private secondInputValue: any;

  constructor(
    @Inject(WINDOW_TOKEN) private $window: Window,
    protected changedDocumentService: ChangedDocumentService,
    private sclUtilDocument: SclUtilDocumentService,
    private el: ElementRef,
    protected formbirdInjectorService: FormbirdInjectorService,
    private deviceDetectService: DeviceDetectService,
    protected cdRef: ChangeDetectorRef,
    protected componentSetupService: ComponentSetupService,
    private finaliseValueService: FinaliseValueFieldService
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdRef);

    this.modifiedFieldService = this.formbirdInjectorService.get('ModifiedFieldService');
    this.notificationService = this.formbirdInjectorService.get('NotificationService');
  }

  ngOnInit() {

    const detect: boolean = this.deviceDetectService.detectMobile();
    if (detect) {
      this.isMobileDetect = true;
    }

    const momentFormat = 'DD/MM/YYYY hh:mm A';

    this.formControl = this.componentSetupService.setupStandardComponent(this);

    let noCalendar = false;
    if (!(typeof this.componentDefinition.noCalendar === 'undefined' || this.componentDefinition.noCalendar === null)) {
      noCalendar = this.componentDefinition.noCalendar;
    }

    let allowInput = true;
    if (!(typeof this.componentDefinition.allowInput === 'undefined' || this.componentDefinition.allowInput === null)) {
      allowInput = this.componentDefinition.allowInput;
    }

    this.displayFormat = 'd/m/Y G:i K';
    if (!(typeof this.componentDefinition.displayFormat === 'undefined' || this.componentDefinition.displayFormat === null ||
      this.componentDefinition.displayFormat === '')) {
      this.displayFormat = this.componentDefinition.displayFormat;
    }
    let enableTime24hr = false;
    if (!(typeof this.componentDefinition.time_24hr === 'undefined' || this.componentDefinition.time_24hr === null)) {
      enableTime24hr = this.componentDefinition.time_24hr;
    }
    this.initializeFieldValue();

    this.flatPicker = flatpickr(this.el.nativeElement.querySelector('.date-time-class'), {
      enableTime: true,
      enableSeconds: (this.displayFormat).toUpperCase().includes('S') || (this.displayFormat).toUpperCase().includes('Z'),
      noCalendar: noCalendar,
      time_24hr: enableTime24hr,
      wrap: true,
      clickOpens: !allowInput,
      allowInput: allowInput,
      defaultDate: this.defaultDateValue,
      defaultHour: this.defaultHourValue,
      defaultMinute: this.defaultMinValue,
      defaultSeconds: this.defaultSecValue,
      minuteIncrement: 1,
      dateFormat: 'Z',
      altInput: true,
      altInputClass: 'mat-input-element mat-form-field-autofill-control input-flat-picker',
      altFormat: this.displayFormat,
      position: 'auto',
      onReady: (dateObj, dateStr, instance) => {
        if(!this.isMobileDetect){
          this.fieldCheckerForFlatPickerInputs(instance);
        }
        const clear = document.createElement('button');
        const clearSpanElement = document.createElement('span');
        clear.className = 'mat-button';
        clearSpanElement.innerHTML = 'Clear';
        clear.append(clearSpanElement);
        clear.addEventListener('click', () => {
          instance.clear();
          instance.set('defaultHour', new Date().getHours());
          instance.set('defaultMinute', new Date().getMinutes());
          instance.set('defaultSeconds', new Date().getSeconds());
          this.flatPicker._input.value = null;
          this.epochDocFieldValue = null;
          this.fieldValue = null;
          delete this.document[this.fieldName];
          this.valueChanged(undefined);
          instance.close();
        });

        const now = document.createElement('button');
        const nowSpanElement = document.createElement('span');
        now.className = 'mat-button';
        nowSpanElement.innerHTML = 'Now';
        now.append(nowSpanElement);
        now.addEventListener('click', () => {
          const dateNow = new Date();
          this.epochDocFieldValue = dateNow.valueOf();
          instance.setDate(dateNow, true);
          instance.set('defaultHour', dateNow.getHours());
          instance.set('defaultMinute', dateNow.getMinutes());
          instance.set('defaultSeconds', dateNow.getSeconds());
          instance.close();
          if (allowInput) {
            this.flatPicker._input.focus();
            this.flatPicker.element.classList.add('mat-focused');
          }
          setTimeout(() => {
            if ((this.displayFormat).toUpperCase().includes('Z')) {
              this.flatPicker._input.value = new Date(dateNow).toISOString();
            }
          });
        });

        const done = document.createElement('button');
        const doneSpanElement = document.createElement('span');
        done.className = 'mat-button';
        doneSpanElement.innerHTML = 'Done';
        done.append(doneSpanElement);
        done.addEventListener('click', () => {
          instance.close();
          this.flatPicker.element.classList.remove('mat-focused');
        });

        if (instance && instance.calendarContainer) {
          const calendarButtonDivWrapper = document.createElement('div');
          calendarButtonDivWrapper.className = 'sc-date-time-controls f-flex f-flex-justify-space-between';
          calendarButtonDivWrapper.append(clear);
          calendarButtonDivWrapper.append(now);
          calendarButtonDivWrapper.append(done);
          instance.calendarContainer.append(calendarButtonDivWrapper);
        }

        document.getElementById('ft-sidenav-content')
          .addEventListener('scroll', (event) => {
            instance._positionCalendar();
          });


        // Event focus, keyup and blur are used in floating label*/
        this.el.nativeElement.querySelector('.input-flat-picker').addEventListener('focus', () => {
          this.flatPicker.element.classList.add('mat-form-field-should-float');
          this.flatPicker.element.classList.add('mat-focused');
          if (this.flatPicker._input.value === '') {
            this.fieldValue = ' ';
          }
          this.oldDateValue = this.flatPicker._input.value;
        });
        this.el.nativeElement.querySelector('.input-flat-picker').addEventListener('keyup', (eventKey) => {
          if (eventKey.keyCode === 8) {
            return;
          }
          const dateEntry = moment(this.flatPicker._input.value, momentFormat);
          if (this.flatPicker._input.value.trim() === '' || this.flatPicker._input.value.trim() === this.oldDateValue) {
            return;
          }
          if (dateEntry.isValid()) {
            // scope.isKeyEntry = true;
            this.fieldValue = moment(dateEntry).toDate();
            this.valueChanged(dateEntry);
          }


        });

        // DION Debug logging
        this.el.nativeElement.querySelector('.input-flat-picker').addEventListener('blur', () => {
          const dateInput = this.flatPicker._input.value.trim();
          // logger.info('Date Input is ^' + dateInput + '^');
          const dateEntry = moment(dateInput, momentFormat);
          if (!isNaN(dateEntry.valueOf())) {
            // logger.info('Original Date Conversion successful result is ' + dateEntry.format());
            const newEntryDate = moment(dateEntry).toDate();
            if (newEntryDate.getMilliseconds() > 0) {
              // logger.info('Original Date Conversion has milliseconds - value is ' + newEntryDate.valueOf());
              this.epochDocFieldValue = newEntryDate.valueOf();
            }
          }
          // DION - End debug logging
          if (!dateInput) {
            this.flatPicker.element.classList.remove('mat-form-field-should-float');
            this.flatPicker.element.classList.remove('mat-focused');
            delete this.document[this.fieldName];
            this.valueChanged(undefined);
          } else {
            this.flatPicker.element.classList.remove('mat-focused');
          }

        });
        // setTimeout(() => {
        //   if ((this.displayFormat).toUpperCase().includes('Z')) {
        //     this.flatPicker._input.value = new Date(this.fieldValue).toISOString();
        //   }
        // });
      },
      onChange: (selectedDates, dateStr, instance) => {
        // Changed to use DateFns - adding back in seconds and milliseconds
        this.onDateTimePickerChangeOrUpdate('onChange', allowInput, noCalendar, dateStr, selectedDates, instance);

      },
      onOpen: (selectedDates, dateStr, instance) => {
        if (!this.fieldValue) {
          setTimeout(() => {
            const dateNow = new Date();
            instance.set('defaultHour', dateNow.getHours());
            instance.set('defaultMinute', dateNow.getMinutes());
            instance.set('defaultSeconds', dateNow.getSeconds());
            this.epochDocFieldValue = dateNow.valueOf();
            this.updateFlatpickrInputs(instance, dateNow);
          });
        }
        document.querySelector('body').classList.add('scroll-hide-date-time-cls');
        if (!allowInput && noCalendar) {
          instance.config.allowInput = true;
          this.flatPicker._input.focus();
        }
      },
      onClose: (selectedDates, dateStr, instance) => {
        document.querySelector('body').classList.remove('scroll-hide-date-time-cls');
        if (!allowInput && noCalendar) {
          instance.config.allowInput = false;
        }
        setTimeout(() => {
          if ((this.displayFormat).toUpperCase().includes('Z') && this.fieldValue) {
            this.flatPicker._input.value = new Date(this.fieldValue).toISOString();
          }
        });
      }
    });

    this.setupChangeDetection(true);

    if (this.componentDefinition.defaultValue) {
      const isNew = this.sclUtilDocument.isNew(this.document);
      if (isNew) {
        this.fieldValue = new Date(this.componentDefinition.defaultValue);
      }
    }

    if (this.componentDefinition.defaultNow === 'New') {
      const isNew = this.sclUtilDocument.isNew(this.document);
      if (isNew && !this.fieldValue) {
        this.fieldValue = new Date();
        const defaultNowNewFinaliseValueFunction = (doc) => {
          doc[this.fieldName] = this.fieldValue;
          if (this.formControl) {
            this.formControl.patchValue(doc[this.fieldName], {emitEvent: false, onlySelf: true});
          }
        };
        this.finaliseValueService.addFinaliseValueFunction(this.template.documentId, this.fieldName, defaultNowNewFinaliseValueFunction);
      }
    } else if (this.componentDefinition.defaultNow === 'Save') {
      const scDateTimeFinaliseValueFunction = (doc) => {
        doc[this.fieldName] = new Date();
        if (this.formControl) {
          this.formControl.patchValue(doc[this.fieldName], {emitEvent: false, onlySelf: true});
        }
      };

      this.finaliseValueService.addFinaliseValueFunction(this.template.documentId, this.fieldName, scDateTimeFinaliseValueFunction);

    }

    this.setFieldActivation(false);

    if (this.fieldValue) {
      if (Object.prototype.toString.call(this.fieldValue) === '[object Date]' && !isNaN(this.fieldValue)) {
        this.currentDateStr = this.fieldValue.toISOString();
      } else {
        const timestamp = Date.parse(this.fieldValue);
        if (isNaN(timestamp) === false) {
          this.currentDateStr = (new Date(timestamp)).toISOString();
        }
      }
    }
    if (this.formControl && this.flatPicker) {
      this.flatPicker.setDate(this.fieldValue);
      this.formControl.patchValue(this.fieldValue, {emitEvent: false, onlySelf: true});
      setTimeout(() => {
        if ((this.displayFormat).toUpperCase().includes('Z') && this.fieldValue) {
          this.flatPicker._input.value =  new Date(this.fieldValue).toISOString();
        }
      });
    }
    // console.log('this.currentDateStr = ' + this.currentDateStr);

    const documentChangedInfo: DocumentChangedInfo = {
      documentId: this.document.documentId,
      fieldName: this.componentDefinition.name,
      newValue: this.fieldValue,
      isInitValue: true
    };
    this.changedDocumentService.notifyDocumentChanged(documentChangedInfo);
  }

  onDateTimePickerChangeOrUpdate(type, allowInput, noCalendar, dateStr, selectedDates, instance) {
    // console.log(type, allowInput, noCalendar, dateStr, selectedDates, instance);
    if (dateStr) {
      const newSelectedDates = selectedDates[0];
      if (this.fieldValue) {
        let oldDateField = null;
        // const parsedDate = global.moment(this.fieldValue,
        //   ['DD/MM/YYYY hh:mm a', 'DD/MM/YYYY', 'hh:mm a', 'DD/MM/YY', 'DD/MM/YY hh:mm a', 'DD/MM', global.moment.ISO_8601],
        //   true);
        // // console.log(parsedDate.toDate().toISOString(), selectedDates[0].toISOString());
        // if (parsedDate.isValid()) {
        //   oldDateField = parsedDate.toDate();
        // } else {
        //   oldDateField = new Date(this.fieldValue);
        // }
        oldDateField = new Date(this.epochDocFieldValue);

        if (newSelectedDates.getSeconds() === 0) {
          newSelectedDates.setSeconds(oldDateField.getSeconds());
        }
        if (newSelectedDates.getMilliseconds() === 0) {
          newSelectedDates.setMilliseconds(oldDateField.getMilliseconds());
        }
         // console.log(newSelectedDates.toISOString(),oldDateField.toISOString());
        this.onDateChange(newSelectedDates.toISOString());
      } else {
        const nowDate = new Date(this.epochDocFieldValue);
        if (newSelectedDates.getSeconds() === 0) {
          newSelectedDates.setSeconds(nowDate.getSeconds());
        }
        if (newSelectedDates.getMilliseconds() === 0) {
          newSelectedDates.setMilliseconds(nowDate.getMilliseconds());
        }
        // console.log('fieldValue undefined',newSelectedDates);
        this.onDateChange(newSelectedDates.toISOString());
      }
       this.document[this.fieldName]= newSelectedDates.toISOString();
    } else {
      delete this.document[this.fieldName];
      this.valueChanged(undefined);
    }
  }

  onDateChange(dateStr) {
    this.debounce(() => {
      if (dateStr) {
        const newDateStr = new Date(dateStr).toISOString();
        // console.log('**** onDateChange, newDateStr = ' + newDateStr + ', currentDateStr = ' + this.currentDateStr);
        if (this.currentDateStr !== newDateStr) {
          this.currentDateStr = dateStr;
          const newSelectedDate = moment(dateStr);
          this.valueChanged(newSelectedDate);
          this.fieldValue = newSelectedDate;
          // console.log('****** onDateChange, dateStr = ' + dateStr + ', newSelectedDate = ' + newSelectedDate.toISOString()
          // + ', this.fieldValue = ' + this.fieldValue.toISOString());

          // Checking if current date on doc is the same as new date.  If so, don't set the date in the document
          let checkDocUnix = 0;
          let checkNewUnix = 0;
          if (this.fieldValue) {
            checkDocUnix = moment(this.document[this.fieldName]).valueOf();
          }
          if (dateStr) {
            checkNewUnix = moment(newSelectedDate).valueOf();
          }
          // if (checkDocUnix !== checkNewUnix) {
          //   this.fieldValue = newSelectedDate.toISOString();
          // }

		  /*
          console.log('Flatpickr OnChange Date[' + this.fieldName + '] has changed -' +
            ' Doc(' + this.fieldValue + ') ' +
            ' New(' + newSelectedDate + ') ' +
            ' DocISO(' + moment(this.fieldValue).toISOString() + ') ' +
            ' NewISO(' + newSelectedDate.toISOString() + ') ' +
            ' DocUNX(' + checkDocUnix + ') ' +
            ' NewUNX(' + checkNewUnix + ') '
          );
		  */
        }
        setTimeout(() => {
          if ((this.displayFormat).toUpperCase().includes('Z') && this.fieldValue) {
            this.flatPicker._input.value = new Date(this.fieldValue).toISOString();
          }
        });
         this.document[this.fieldName]= new Date(this.fieldValue).toISOString();
      } else {
        delete this.document[this.fieldName];
        this.valueChanged(undefined);
      }
    }, 500);
  }

  initializeFieldValue() {
    if (this.fieldValue) {
      this.defaultDateValue = this.fieldValue;
      const val = new Date(this.defaultDateValue);
      this.defaultHourValue = val.getHours();
      this.defaultMinValue = val.getMinutes();
      this.defaultSecValue = val.getSeconds();
      this.epochDocFieldValue = val.valueOf();

    } else {

      if (!this.sclUtilDocument.isNew(this.document)) {
        this.defaultDateValue = '';
        this.defaultHourValue = new Date().getHours();
        this.defaultMinValue = new Date().getMinutes();
        this.defaultSecValue = new Date().getSeconds();

      } else {

        if ((this.componentDefinition.defaultNow !== 'New')) {
          this.defaultDateValue = '';
          this.defaultHourValue = new Date().getHours();
          this.defaultMinValue = new Date().getMinutes();
          this.defaultSecValue = new Date().getSeconds();

        } else {
          this.fieldValue = new Date();
          this.defaultDateValue = this.fieldValue;
          const val = new Date(this.defaultDateValue);
          this.defaultHourValue = val.getHours();
          this.defaultMinValue = val.getMinutes();
          this.defaultSecValue = val.getSeconds();
          this.epochDocFieldValue = val.valueOf();
        }
      }
      if(this.isMobileDetect){
        this.epochDocFieldValue = new Date().valueOf();
      }
    }
  }

  private debounce(func, timer) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      func();
    }, timer);
  };

  onControlValueChanged(value) {
    // do onChange instead
  }

  onFieldValueChanged(fieldValue) {
    if (this.fieldValue === fieldValue) {
      return;
    }
    const parsedDate = global.moment(fieldValue,
      ['DD/MM/YYYY hh:mm a', 'DD/MM/YYYY', 'hh:mm a', 'DD/MM/YY', 'DD/MM/YY hh:mm a', 'DD/MM', global.moment.ISO_8601],
      true);
    if (parsedDate.isValid()) {
      this.fieldValue = parsedDate.toDate();
    } else {
      this.fieldValue = fieldValue;
    }
    this.flatPicker.setDate(this.fieldValue);
    this.formControl.patchValue(this.fieldValue, {emitEvent: false, onlySelf: true});
    this.document[this.fieldName]= new Date(this.fieldValue).toISOString();
    setTimeout(() => {
      if ((this.displayFormat).toUpperCase().includes('Z') && this.fieldValue) {
        this.flatPicker._input.value = new Date(this.fieldValue).toISOString();
      }
    });
    const getDateFieldValue = new Date(this.fieldValue);
    this.defaultHourValue = getDateFieldValue.getHours();
    this.defaultMinValue = getDateFieldValue.getMinutes();
    this.defaultSecValue = getDateFieldValue.getSeconds();
    this.epochDocFieldValue = getDateFieldValue.valueOf();
    this.detectChanges();
  }

  setFieldActivation(shouldDetectChange?) {

    if (this.disabled) {

      this.formControl.disable();

      if (this.flatPicker && this.flatPicker._input) {
        this.flatPicker._input.setAttribute('disabled', 'disabled');
      }

      if (this.isMobileDetect) {
        const dateInput = this.el.nativeElement.querySelector('.date-time-class input.flatpickr-mobile');
        dateInput.setAttribute('disabled', 'disabled');
      }

    } else {

      this.formControl.enable();

      if (this.flatPicker && this.flatPicker._input) {
        this.flatPicker._input.removeAttribute('disabled');
      }

      if (this.isMobileDetect) {
        const dateInput = this.el.nativeElement.querySelector('.date-time-class input.flatpickr-mobile');
        dateInput.removeAttribute('disabled');
      }
    }

    this.detectChanges(shouldDetectChange);

  }

  openCalendar() {
    this.flatPicker.toggle();
  }

  showToolTip(tooltip: MatTooltip, formFieldContainer: MatFormField, labelContainer: HTMLElement) {
    return (labelContainer.offsetWidth <= formFieldContainer._elementRef.nativeElement.scrollWidth);
  }

  toggleTooltip(tooltip: MatTooltip) {
    setTimeout(() => {
      tooltip.toggle();
    });
  }

  valueChanged(newValue) {
    if (newValue) {
      const newFieldValueStr = new Date(newValue).toISOString();
      const newFieldValueDate = moment(newFieldValueStr);
      if ((this.displayFormat).toUpperCase().includes('S')) {
        // Do not truncate the seconds and truncate milliseconds.
        newFieldValueDate.milliseconds(0);
      }else if ((this.displayFormat).toUpperCase().includes('Z')) {
        // Do not truncate both the seconds and milliseconds if displayFormat is 'Z'
      }else{
        // truncate both the seconds and milliseconds.
          newFieldValueDate.seconds(0);
          newFieldValueDate.milliseconds(0);
      }
      super.valueChanged(newFieldValueDate.toDate());
      this.currentDateStr = newFieldValueDate.toISOString();
      this.document[this.fieldName]= newFieldValueDate.toISOString();
    } else {
      delete this.document[this.fieldName];
      super.valueChanged(undefined);
      this.currentDateStr = null;
    }
  }

  updateFlatpickrInputs(instance, dateNow) {
    if (instance.hourElement) {
      const hourElementInputs = [
        instance.hourElement
      ];
      hourElementInputs.forEach(input => {
        const hourVal = dateNow.getHours();
        input.value = ('0' + dateNow.getHours()).slice(-2);
        if (hourVal > 12) {
          input.value = ('0' + (hourVal - 12)).slice(-2);
        } else if (hourVal === 12) {
          input.value = ('0' + hourVal).slice(-2);
        }
      });
    }
    if (instance.minuteElement) {
      const minuteElementInputs = [
        instance.minuteElement
      ];
      minuteElementInputs.forEach(input => {
        input.value = ('0' + dateNow.getMinutes()).slice(-2);
      });
    }
    if (instance.secondElement) {
      const secondElementInputs = [
        instance.secondElement
      ];
      secondElementInputs.forEach(input => {
        input.value = ('0' + dateNow.getSeconds()).slice(-2);
      });
    }
  }

  fieldCheckerForFlatPickerInputs(instance) {
    const hourElementInputs = [
      instance.hourElement
    ];
    hourElementInputs.forEach(input => {
      input.addEventListener('input', e => {
        const {value} = e.target;
        // console.log('input', input, value, instance);
        if (value) {
          this.hourInputValue = value;
        }
      });
      input.addEventListener('blur', e => {
        if (this.hourInputValue) {
          this.processHourAndMinutes('hours', this.hourInputValue, instance);
        }
      });
    });
    const minuteElementInputs = [
      instance.minuteElement
    ];
    minuteElementInputs.forEach(input => {
      input.addEventListener('input', e => {
        const {value} = e.target;
        // console.log('input', input, value, instance);
        if (value) {
          this.minuteInputValue = value;
        }
      });
      input.addEventListener('blur', e => {
        if (this.minuteInputValue) {
          this.processHourAndMinutes('minutes', this.minuteInputValue, instance);
        }
      });
    });
    if (instance.secondElement) {
      const secondElementInputs = [
        instance.secondElement,
      ];
      secondElementInputs.forEach(input => {
        input.addEventListener('input', e => {
          const {value} = e.target;
          // console.log('input', input, value, instance);
          if (value) {
            this.secondInputValue = value;
          }
        });
        input.addEventListener('blur', e => {
          const {value} = e.target;
           // console.log('blur sec', input, value, instance, this.secondInputValue);
          if (this.secondInputValue) {
            this.processHourAndMinutes('seconds', this.secondInputValue, instance);
          }
        });
      });
    }
    if (instance.amPM) {
      const amPmElementInputs = [
        instance.amPM,
      ];
      amPmElementInputs.forEach(elem => {
        elem.addEventListener('keyup', e => {
          // console.log('amPm keyUp event', elem, elem.textContent, e, value, instance);
          const fieldValDate = global.moment(this.fieldValue,
            ['DD/MM/YYYY hh:mm a', 'DD/MM/YYYY', 'hh:mm a', 'DD/MM/YY', 'DD/MM/YY hh:mm a', 'DD/MM', global.moment.ISO_8601],
            true);
          let newTempDate: any = null;
          if (fieldValDate.isValid()) {
            newTempDate = fieldValDate.toDate();
          } else {
            newTempDate = new Date(this.fieldValue);
          }
          const getTempDateLocaleString = newTempDate.toLocaleString('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
          });
          if (e.key.toLowerCase() === 'a') {
            if (getTempDateLocaleString.split(' PM').length > 1) {
              this.processHourAndMinutes('amPM', -12, instance);
            }
          } else if (e.key.toLowerCase() === 'p') {
            if (getTempDateLocaleString.split(' AM').length > 1) {
              this.processHourAndMinutes('amPM', 12, instance);
            }
          }
        });
      });
    }
  }

  processHourAndMinutes(type, value, instance) {
    let newDateStrValue: any;
    if (this.fieldValue) {
      const parsedDate = global.moment(this.fieldValue,
        ['DD/MM/YYYY hh:mm a', 'DD/MM/YYYY', 'hh:mm a', 'DD/MM/YY', 'DD/MM/YY hh:mm a', 'DD/MM', global.moment.ISO_8601],
        true);
      if (parsedDate.isValid()) {
        newDateStrValue = parsedDate.toDate();
      } else {
        newDateStrValue = new Date(this.fieldValue);
      }
    } else {
      newDateStrValue = instance.now;
    }
    if (type === 'hours') {
      const getTimeLocaleString = newDateStrValue.toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });
      logger.log('Flatpickr hours field[' + this.fieldName + '] input value: ', value);
      if (instance.amPM) {
        if (getTimeLocaleString.split(' PM').length > 1 && (+value) < 12) {
          const newHour = +value + 12;
          newDateStrValue.setHours(newHour);
        } else if (getTimeLocaleString.split(' AM').length > 1 && (+value) === 12) {
          newDateStrValue.setHours(0);
        } else {
          newDateStrValue.setHours(value);
        }
      } else {
        newDateStrValue.setHours(value);
      }
      this.hourInputValue = null;
    } else if (type === 'minutes') {
      logger.log('Flatpickr minutes field[' + this.fieldName + '] input value: ', value);
      this.minuteInputValue = null;
      newDateStrValue.setMinutes(value);
    } else if (type === 'seconds') {
      logger.log('Flatpickr seconds field[' + this.fieldName + '] input value: ', value);
      this.secondInputValue = null;
      newDateStrValue.setSeconds(value);
    } else if (type === 'amPM') {
      const tempGetHour = newDateStrValue.getHours();
      newDateStrValue.setHours(+tempGetHour + +value);
    }
    this.onDateChange(newDateStrValue.toISOString());
    if (this.formControl && this.flatPicker) {
      this.flatPicker.setDate(newDateStrValue);
      this.formControl.patchValue(newDateStrValue, {emitEvent: false, onlySelf: true});
      this.detectChanges();
    }
  }
}
